import { NamingElement, NamingSeparator, TemplateColumn, TemplateFileFormat } from '../../../types/packager-types';

export interface NamingPreviewParams {
  namingElements: NamingElement[];
  namingSeparator: NamingSeparator;
  templateName: string;
  fileFormat: TemplateFileFormat;
  splitBy?: string;
}

// Placeholder date shown in previews (format: YYYY-MM-DD)
const PREVIEW_DATE_PLACEHOLDER = 'YYYY-MM-DD';

// Valid date format directives (matching backend)
const VALID_DATE_DIRECTIVES = new Set([
  '%Y', '%m', '%d', '%H', '%M', '%S',  // Basic date/time
  '%y', '%b', '%B',  // Variations
  '%j', '%U', '%W',  // Special formats
  '%c', '%x', '%X',  // Locale formats
  '%A', '%a', '%I', '%p',  // Weekday names, 12-hour format, AM/PM
  '%f', '%z', '%Z',  // Microseconds and timezone
  '%w', '%V',  // Additional valid directives
]);

// Valid separator characters for date formats (matching backend)
const VALID_DATE_FORMAT_SEPARATORS = new Set(['-', '_', '/', '.', ':', ' ']);

export interface DateFormatValidationResult {
  isValid: boolean;
  error: string;
}

/**
 * Validates a date format string according to backend rules
 */
export function validateDateFormat(formatStr: string | undefined): DateFormatValidationResult {
  if (formatStr === undefined || formatStr === null) {
    return { isValid: true, error: '' };
  }

  if (formatStr === '') {
    return { isValid: false, error: 'Date format cannot be empty' };
  }

  try {
    // Split format string into directives and separators
    const parts = formatStr.split(/(%[a-zA-Z])/).filter(p => p);

    for (const part of parts) {
      if (part.startsWith('%')) {
        // Validate directive
        if (!VALID_DATE_DIRECTIVES.has(part)) {
          return {
            isValid: false,
            error: `Invalid directive '${part}'`,
          };
        }
      } else {
        // Validate separator characters
        for (const char of part) {
          if (!VALID_DATE_FORMAT_SEPARATORS.has(char)) {
            return {
              isValid: false,
              error: `Invalid separator '${char}'`,
            };
          }
        }
      }
    }
  } catch {
    return { isValid: false, error: 'Invalid date format' };
  }

  return { isValid: true, error: '' };
}

/**
 * Generates a preview of the filename based on naming configuration
 */
export function generateNamingPreview({
  namingElements,
  namingSeparator,
  templateName,
  fileFormat,
  splitBy,
}: NamingPreviewParams): string {
  if (namingElements.length === 0) {
    return `${templateName || 'template_name'}_${PREVIEW_DATE_PLACEHOLDER}.${fileFormat.toLowerCase()}`;
  }
  const parts = namingElements.map((el) => {
    if (el.type === 'STRING_LITERAL') return el.value || 'text';
    if (el.type === 'METADATA') return el.value?.split('.').pop() || 'metadata';
    if (el.type === 'DATE_FORMAT') return el.format || PREVIEW_DATE_PLACEHOLDER;
    if (el.type === 'DOCUMENT_DATA') return el.value || 'field';
    if (el.type === 'SPLIT_VALUE') return splitBy ? `{${splitBy}}` : '{split_value}';
    return '';
  });
  return `${parts.join(namingSeparator)}.${fileFormat.toLowerCase()}`;
}

export interface ValidationResult {
  isValid: boolean;
  nameError: string;
  columnsError: string;
  namingError: string;
}

export interface ValidateTemplateParams {
  templateName: string;
  existingTemplateNames: string[];
  editingTemplateName: string | null;
  columns: TemplateColumn[];
  namingElements: NamingElement[];
  namingMaxLength: number;
}

/**
 * Validates template configuration and returns validation errors
 */
export function validateTemplate({
  templateName,
  existingTemplateNames,
  editingTemplateName,
  columns,
  namingElements,
  namingMaxLength,
}: ValidateTemplateParams): ValidationResult {
  const result: ValidationResult = {
    isValid: true,
    nameError: '',
    columnsError: '',
    namingError: '',
  };

  const trimmedName = templateName.trim();

  // Validate template name
  if (!trimmedName) {
    result.isValid = false;
    result.nameError = 'Template name is required';
    return result;
  }

  if (
    existingTemplateNames.includes(trimmedName) &&
    (!editingTemplateName || editingTemplateName !== trimmedName)
  ) {
    result.isValid = false;
    result.nameError = 'A template with this name already exists';
    return result;
  }

  // Validate columns
  if (columns.length === 0) {
    result.isValid = false;
    result.columnsError = 'At least one column is required';
    return result;
  }

  const hasEmptyField = columns.some((c) => !c.field || c.field.trim() === '');
  if (hasEmptyField) {
    result.isValid = false;
    result.columnsError = 'All columns must have a field selected';
    return result;
  }

  const hasEmptyHeader = columns.some((c) => !c.header || c.header.trim() === '');
  if (hasEmptyHeader) {
    result.isValid = false;
    result.columnsError = 'All columns must have a header name';
    return result;
  }

  // Validate no duplicate headers
  const headers = columns.map((c) => c.header.trim().toLowerCase());
  const uniqueHeaders = new Set(headers);
  if (uniqueHeaders.size !== headers.length) {
    result.isValid = false;
    result.columnsError = 'Column headers must be unique';
    return result;
  }

  // Validate naming config if present
  if (namingElements.length > 0) {
    if (namingMaxLength < 10 || namingMaxLength > 255) {
      result.isValid = false;
      result.namingError = 'Max length must be between 10 and 255';
      return result;
    }

    // SPLIT_VALUE elements derive their content from the Split By field and have no user-entered value
    const hasEmptyNamingValue = namingElements.some(
      (el) => el.type !== 'SPLIT_VALUE' && (!el.value || el.value.trim() === ''),
    );
    if (hasEmptyNamingValue) {
      result.isValid = false;
      result.namingError = 'All naming elements must have a value';
      return result;
    }

    // Validate date formats for date metadata fields
    for (const el of namingElements) {
      if (el.type === 'METADATA' &&
          (el.value === 'document.created_at' || el.value === 'document.updated_at')) {
        const formatValidation = validateDateFormat(el.format);
        if (!formatValidation.isValid) {
          result.isValid = false;
          result.namingError = formatValidation.error;
          return result;
        }
      }
    }
  }

  return result;
}
